import { useState, useEffect, useCallback } from 'react';
import { locationDropdownService, State, City } from '../services/locationDropdownService';

export interface UseLocationDropdownsReturn {
  // States
  states: State[];
  cities: City[];
  selectedState: string;
  selectedCity: string;
  isLoadingStates: boolean;
  isLoadingCities: boolean;
  error: string | null;

  // Actions
  setSelectedState: (stateIso2: string) => void;
  setSelectedCity: (cityName: string) => void;
  loadStates: () => Promise<void>;
  loadCities: (stateIso2: string) => Promise<void>;
  clearCities: () => void;
  reset: () => void;

  // Getters
  getStateName: (stateIso2: string) => string;
  getStateIso2: (stateName: string) => string;
}

export const useLocationDropdowns = (): UseLocationDropdownsReturn => {
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [isLoadingStates, setIsLoadingStates] = useState<boolean>(false);
  const [isLoadingCities, setIsLoadingCities] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load all states on hook initialization
  const loadStates = useCallback(async () => {
    setIsLoadingStates(true);
    setError(null);

    try {
      const statesData = await locationDropdownService.getStates();
      setStates(statesData);
    } catch (err: any) {
      setError(err.message || 'Failed to load states');
      console.error('Error loading states:', err);
    } finally {
      setIsLoadingStates(false);
    }
  }, []);

  // Load cities for a specific state
  const loadCities = useCallback(async (stateIso2: string) => {
    if (!stateIso2) {
      setCities([]);
      return;
    }

    setIsLoadingCities(true);
    setError(null);

    try {
      const citiesData = await locationDropdownService.getCitiesByState(stateIso2);
      setCities(citiesData);
    } catch (err: any) {
      setError(err.message || 'Failed to load cities');
      console.error('Error loading cities:', err);
    } finally {
      setIsLoadingCities(false);
    }
  }, []);

  // Handle state selection
  const handleStateChange = useCallback((stateIso2: string) => {
    setSelectedState(stateIso2);
    setSelectedCity(''); // Clear city when state changes
    
    if (stateIso2) {
      loadCities(stateIso2);
    } else {
      setCities([]);
    }
  }, [loadCities]);

  // Clear cities
  const clearCities = useCallback(() => {
    setCities([]);
    setSelectedCity('');
  }, []);

  // Reset all selections
  const reset = useCallback(() => {
    setSelectedState('');
    setSelectedCity('');
    setCities([]);
    setError(null);
  }, []);

  // Get state name by ISO2 code
  const getStateName = useCallback((stateIso2: string): string => {
    const state = states.find(s => s.iso2 === stateIso2);
    return state?.name || stateIso2;
  }, [states]);

  // Get state ISO2 code by name
  const getStateIso2 = useCallback((stateName: string): string => {
    const state = states.find(s => s.name.toLowerCase() === stateName.toLowerCase());
    return state?.iso2 || '';
  }, [states]);

  // Load states on mount
  useEffect(() => {
    loadStates();
  }, [loadStates]);

  return {
    // States
    states,
    cities,
    selectedState,
    selectedCity,
    isLoadingStates,
    isLoadingCities,
    error,

    // Actions
    setSelectedState: handleStateChange,
    setSelectedCity,
    loadStates,
    loadCities,
    clearCities,
    reset,

    // Getters
    getStateName,
    getStateIso2,
  };
};
